import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useBrands, useDeleteBrand, useReorderBrands } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { errorMessage } from "@/lib/error-message";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface BrandRow {
  id: string;
  name: string;
  slug: string;
  slugEn?: string | null;
  order: number;
  logoUrl: string;
  featured: boolean;
}

function SortableRow({
  brand,
  dragDisabled,
  onEdit,
  onDelete,
}: {
  brand: BrandRow;
  dragDisabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: brand.id,
    disabled: dragDisabled,
  });

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && "relative z-10 bg-muted/60 shadow-md")}
    >
      <TableCell className="text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            {...attributes}
            {...listeners}
            disabled={dragDisabled}
            className={cn(
              "p-1 rounded text-muted-foreground/60 hover:text-foreground",
              dragDisabled ? "cursor-not-allowed opacity-30" : "cursor-grab active:cursor-grabbing",
            )}
            title={dragDisabled ? "Aramada sıralama yapılamaz" : "Sürükleyerek sırala"}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {brand.order}
        </div>
      </TableCell>
      <TableCell>
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt={brand.name} className="h-10 w-10 object-contain rounded" />
        ) : (
          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
            ?
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">{brand.name}</TableCell>
      <TableCell className="text-muted-foreground text-sm">
        <div>{brand.slug}</div>
        {brand.slugEn && brand.slugEn !== brand.slug && (
          <div className="truncate text-xs text-muted-foreground/70">EN: {brand.slugEn}</div>
        )}
      </TableCell>
      <TableCell>
        {brand.featured ? (
          <Badge variant="default">Evet</Badge>
        ) : (
          <Badge variant="outline">Hayır</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function BrandList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const {
    data: rawBrands,
    isLoading,
    isError,
    error,
    refetch,
  } = useBrands(debouncedSearch || undefined);
  const deleteMutation = useDeleteBrand();
  const reorderMutation = useReorderBrands();

  // Sürükleme sırasında anında geri bildirim için yerel kopya
  const [brands, setBrands] = useState<BrandRow[]>([]);
  useEffect(() => {
    setBrands((rawBrands as BrandRow[] | undefined) ?? []);
  }, [rawBrands]);

  // Arama filtresi aktifken sıralama kapalı (kısmi liste yanlış sıra üretir)
  const dragDisabled = !!search.trim() || !!debouncedSearch;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = brands.findIndex((b) => b.id === active.id);
    const newIndex = brands.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = arrayMove(brands, oldIndex, newIndex).map((b, i) => ({
      ...b,
      order: i,
    }));
    setBrands(next);

    reorderMutation.mutate(
      next.map((b) => ({ id: b.id, order: b.order })),
      {
        onSuccess: () => toast.success("Sıralama kaydedildi"),
        onError: (e: unknown) => {
          toast.error(errorMessage(e, "Sıralama kaydedilemedi"));
          setBrands((rawBrands as BrandRow[] | undefined) ?? []);
        },
      },
    );
  }

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Marka silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Markalar</h1>
        <Button onClick={() => navigate("/brands/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Marka
        </Button>
      </div>

      {/* Search */}
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawBrands ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : brands.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {debouncedSearch ? "Arama sonucu bulunamadı." : "Henüz marka eklenmedi."}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Sıra</TableHead>
                <TableHead className="w-16">Logo</TableHead>
                <TableHead>Ad</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-24">Öne Çıkan</TableHead>
                <TableHead className="w-28 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={brands.map((b) => b.id)}
                strategy={verticalListSortingStrategy}
              >
                {brands.map((brand) => (
                  <SortableRow
                    key={brand.id}
                    brand={brand}
                    dragDisabled={dragDisabled}
                    onEdit={() => navigate(`/brands/${brand.id}/edit`)}
                    onDelete={() => handleDelete(brand.id)}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      )}
    </div>
  );
}
